/* eslint-disable no-underscore-dangle */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Button, Card, Form, Modal, OverlayTrigger, Spinner, Table, Tooltip,
} from 'react-bootstrap';
import dayjs from 'dayjs';
import Error from '../../../Components/Error';
import PaymentsService from '../../../Services/PaymentsService';
import counterpartyIdOf from './counterpartyIdOf';
import PartnersService from '../../../Services/PartnersService';
import OperatorService from '../../../Services/OperatorService';
import { useAccountsList } from '../../../Hooks/useAccountsCache';
import { formatDate } from '../../../Utils/timezone';
import occurredAtIso from '../../../Utils/occurredAt';

// Local YYYY-MM-DD for today / first of month. Local zone (not UTC) so
// the date inputs pre-fill with what the admin sees on the wall
// calendar; we convert to a full ISO string only on submit.
function todayYmd() {
  return dayjs().format('YYYY-MM-DD');
}
function startOfMonthYmd() {
  return dayjs().startOf('month').format('YYYY-MM-DD');
}
function endOfMonthYmd() {
  return dayjs().endOf('month').format('YYYY-MM-DD');
}

// Inline money formatter — mirrors the helpers used elsewhere on the
// Payments surface (RecordPaymentModal, Payments index). No shared util
// has been promoted; consolidating would touch too many unrelated files.
function formatMoney(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Number(amount);
  if (currency === 'HUF') return `${Math.round(n).toLocaleString('en-US')} Ft`;
  return `€${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const CURRENCIES = ['EUR', 'HUF'];

// Default direction for a counterparty kind. Operator payouts go OUT
// (we pay them), partner settlements come IN (they wire us). Admin can
// flip the direction radio in either case (refunds, reverse-flows).
function defaultDirectionFor(kind) {
  return kind === 'operator' ? 'out' : 'in';
}

// Truncated 2-decimal rounding that DOES NOT touch the 3rd+ digit —
// used to keep allocation sums clean. parseFloat(toFixed(2)) is the
// idiomatic JS pattern for this; we don't pull in big.js for one site.
function round2(n) {
  return Math.round(n * 100) / 100;
}

function BulkPaymentModal({
  show,
  onHide,
  onRecorded,
  presetCounterparty,
  presetPeriod,
  presetDirection,
  presetCurrency,
}) {
  // --- Counterparty + period + direction --------------------------------

  const [counterpartyKind, setCounterpartyKind] = useState(
    presetCounterparty?.kind || 'operator',
  );
  const [counterpartyId, setCounterpartyId] = useState(presetCounterparty?.id || '');
  const [direction, setDirection] = useState(
    presetDirection || defaultDirectionFor(presetCounterparty?.kind || 'operator'),
  );
  const [periodFrom, setPeriodFrom] = useState(
    presetPeriod?.from || startOfMonthYmd(),
  );
  const [periodTo, setPeriodTo] = useState(
    presetPeriod?.to || endOfMonthYmd(),
  );
  // Default to the row's settlement currency when the caller passes one
  // (e.g. opening from a HUF operator row in Month End). The
  // outstandingLines endpoint filters by currency — defaulting to EUR
  // on a HUF counterparty silently returned zero lines.
  const [currency, setCurrency] = useState(presetCurrency || 'EUR');

  // Preset counterparty/period disables the picker so the admin can't
  // accidentally settle a different counterparty than the row they
  // clicked from in Stats. Direction is intentionally still editable
  // — refunds need the opposite direction.
  const counterpartyLocked = !!presetCounterparty;

  // --- Money ------------------------------------------------------------

  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [occurredAt, setOccurredAt] = useState(todayYmd());
  const [bankReference, setBankReference] = useState('');
  const [notes, setNotes] = useState('');

  // --- Accounts list (shared cache) ------------------------------------

  const { accounts: allAccounts } = useAccountsList();
  const activeAccounts = useMemo(
    () => allAccounts.filter((a) => a.is_active),
    [allAccounts],
  );

  // --- Partner / operator option lists --------------------------------

  const [partners, setPartners] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const partnersRequestedRef = useRef(false);
  const operatorsRequestedRef = useRef(false);

  // --- Outstanding lines fetch state ----------------------------------

  const [outstandingLines, setOutstandingLines] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [linesLoading, setLinesLoading] = useState(false);
  const [linesError, setLinesError] = useState('');
  // Per-line allocations keyed by order_line_key. Stored as numbers so
  // we don't have to re-parse on every render.
  const [allocations, setAllocations] = useState({});

  // --- Submit lifecycle ------------------------------------------------

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  // --- Reset on (re-)open ---------------------------------------------

  useEffect(() => {
    if (!show) return;
    const initialKind = presetCounterparty?.kind || 'operator';
    setCounterpartyKind(initialKind);
    setCounterpartyId(presetCounterparty?.id || '');
    setDirection(presetDirection || defaultDirectionFor(initialKind));
    setPeriodFrom(presetPeriod?.from || startOfMonthYmd());
    setPeriodTo(presetPeriod?.to || endOfMonthYmd());
    setCurrency(presetCurrency || 'EUR');
    setAccountId('');
    setAmount('');
    setOccurredAt(todayYmd());
    setBankReference('');
    setNotes('');
    setOutstandingLines([]);
    setTotalOutstanding(0);
    setTruncated(false);
    setLinesError('');
    setLinesLoading(false);
    setAllocations({});
    setError('');
    setValidationError('');
    setSubmitting(false);
    // Reset the lazy-load guards so a re-open after switching tabs
    // refetches the lists (avoids displaying a stale operator list).
    partnersRequestedRef.current = false;
    operatorsRequestedRef.current = false;
  }, [show, presetCounterparty, presetPeriod, presetDirection, presetCurrency]);

  // --- Lazy-load partner / operator lists -----------------------------

  useEffect(() => {
    if (!show) return undefined;
    if (counterpartyKind !== 'partner') return undefined;
    if (partnersRequestedRef.current) return undefined;
    partnersRequestedRef.current = true;
    let cancelled = false;
    (async () => {
      setLoadingPartners(true);
      try {
        const response = await PartnersService.fetchPartners();
        if (cancelled) return;
        const list = response?.data?.data || [];
        setPartners(Array.isArray(list) ? list : []);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message || 'Failed to load partners');
        partnersRequestedRef.current = false;
      } finally {
        if (!cancelled) setLoadingPartners(false);
      }
    })();
    return () => { cancelled = true; };
  }, [show, counterpartyKind]);

  useEffect(() => {
    if (!show) return undefined;
    if (counterpartyKind !== 'operator') return undefined;
    if (operatorsRequestedRef.current) return undefined;
    operatorsRequestedRef.current = true;
    let cancelled = false;
    (async () => {
      setLoadingOperators(true);
      try {
        const response = await OperatorService.fetchOperators();
        if (cancelled) return;
        const list = response?.data?.data || [];
        setOperators(Array.isArray(list) ? list : []);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message || 'Failed to load operators');
        operatorsRequestedRef.current = false;
      } finally {
        if (!cancelled) setLoadingOperators(false);
      }
    })();
    return () => { cancelled = true; };
  }, [show, counterpartyKind]);

  // --- Outstanding-lines fetch (debounced) ----------------------------
  //
  // Refetch whenever the counterparty/period/direction/currency change.
  // We debounce by 250ms so rapid-fire edits (typing a date) don't fire
  // a stampede of requests. Race protection via a sequence number — if
  // a slower earlier response lands AFTER a newer one started, we drop
  // it on the floor.
  const fetchSeqRef = useRef(0);

  const fetchOutstandingLines = useCallback(async () => {
    if (!counterpartyId || !counterpartyKind || !periodFrom || !periodTo || !direction) {
      setOutstandingLines([]);
      setTotalOutstanding(0);
      setTruncated(false);
      setLinesError('');
      return;
    }
    const mySeq = fetchSeqRef.current + 1;
    fetchSeqRef.current = mySeq;
    setLinesLoading(true);
    setLinesError('');
    try {
      const data = await PaymentsService.listOutstandingLines({
        counterparty_kind: counterpartyKind,
        counterparty_id: counterpartyId,
        period_from: periodFrom,
        period_to: periodTo,
        line_direction: direction,
        currency,
      });
      if (mySeq !== fetchSeqRef.current) return;
      const lines = Array.isArray(data?.lines) ? data.lines : [];
      setOutstandingLines(lines);
      setTotalOutstanding(Number(data?.total_outstanding) || 0);
      setTruncated(!!data?.truncated);
      // Reset allocations on a fresh fetch — old keys may no longer
      // exist, and stale rows would skew the running total.
      setAllocations({});
    } catch (err) {
      if (mySeq !== fetchSeqRef.current) return;
      setLinesError(
        err.response?.data?.message || err.message || 'Failed to load outstanding lines',
      );
      setOutstandingLines([]);
      setTotalOutstanding(0);
      setTruncated(false);
    } finally {
      if (mySeq === fetchSeqRef.current) setLinesLoading(false);
    }
  }, [counterpartyKind, counterpartyId, periodFrom, periodTo, direction, currency]);

  useEffect(() => {
    if (!show) return undefined;
    const handle = setTimeout(() => { fetchOutstandingLines(); }, 250);
    return () => clearTimeout(handle);
  }, [show, fetchOutstandingLines]);

  // --- Derived totals -------------------------------------------------

  const allocationsTotal = useMemo(() => Object.values(allocations).reduce((sum, v) => {
    const n = Number(v);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0), [allocations]);

  const amountNum = Number(amount);
  const amountValid = Number.isFinite(amountNum) && amountNum > 0;
  // 0.005 tolerance — guards against the classic 0.1 + 0.2 !== 0.3 float
  // case without giving real mismatches a free pass.
  const allocationsMatch = amountValid
    && Math.abs(allocationsTotal - amountNum) < 0.005;

  const stillOutstandingAfter = round2(totalOutstanding - allocationsTotal);
  const overflowAmount = round2(amountNum - totalOutstanding);
  const hasOverflow = amountValid && overflowAmount > 0.005;

  // --- Auto-allocate FIFO --------------------------------------------
  //
  // Walks lines top-down (already sorted oldest-first by the backend).
  // For each line, allocate min(remaining, line.outstanding_amount).
  // Last line gets the partial when the amount runs out.

  // Composite allocation key: `${booking_id}:${order_line_key}`. The
  // backend's order_line_key is unique per (kind, operator/service/...)
  // but NOT per booking — e.g. every operator-price line for the same
  // operator across N bookings shares the key "operator:X". Without
  // including booking_id here the auto-allocator would write one entry
  // that then renders the same value in every row sharing the key
  // (visible bug: type 10,000, see 10,000 in every order's input).
  const allocKey = useCallback((line) => `${line?.booking_id ?? ''}:${line?.order_line_key ?? ''}`, []);

  const autoAllocateTopDown = useCallback(() => {
    if (!amountValid) {
      setAllocations({});
      return;
    }
    let remaining = amountNum;
    const next = {};
    outstandingLines.forEach((line) => {
      if (remaining <= 0.005) return;
      const lineOutstanding = Number(line.outstanding_amount) || 0;
      if (lineOutstanding <= 0.005) return;
      // Skip lines whose own currency doesn't match the payment
      // currency — they're surfaced for visibility (e.g. cash_from_client
      // EUR lines for a HUF-settling operator) but can't be allocated
      // until the admin switches the payment currency to match. The
      // UI also disables the per-row input in this case.
      if (line.currency && line.currency !== currency) return;
      const allocate = Math.min(remaining, lineOutstanding);
      next[allocKey(line)] = round2(allocate);
      remaining = round2(remaining - allocate);
    });
    setAllocations(next);
  }, [amountValid, amountNum, outstandingLines, allocKey, currency]);

  // Auto-allocate whenever the amount or lines change. The admin can
  // still override per-row afterwards — typing in a row overwrites that
  // row's allocation without re-running the auto-allocator. We DON'T
  // re-run when `allocations` changes (would create an infinite loop
  // and stomp manual edits).
  useEffect(() => {
    if (!show) return;
    autoAllocateTopDown();
  }, [show, autoAllocateTopDown]);

  // --- Validation -----------------------------------------------------

  const counterpartyValid = !!counterpartyId;
  const basicValid = amountValid
    && !!currency
    && !!occurredAt
    && counterpartyValid
    && !!accountId;

  const allocationValid = allocationsMatch
    && Object.values(allocations).every((v) => {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0;
    });

  const canSubmit = basicValid && allocationValid && !submitting;

  // --- Handlers -------------------------------------------------------

  const handleKindChange = (nextKind) => {
    if (counterpartyLocked) return;
    setCounterpartyKind(nextKind);
    setCounterpartyId('');
    setDirection(defaultDirectionFor(nextKind));
    setAllocations({});
  };

  const handlePerRowAllocate = (key, value) => {
    setAllocations((prev) => ({ ...prev, [key]: value }));
  };

  const handleRowMax = (line) => {
    // "Max" = min(remaining unallocated, line outstanding). Remaining is
    // computed against the CURRENT row's previous value — i.e. as if
    // the row was empty — so clicking max repeatedly is idempotent.
    const key = allocKey(line);
    const lineOutstanding = Number(line.outstanding_amount) || 0;
    const currentRowValue = Number(allocations[key]) || 0;
    const otherAllocations = allocationsTotal - currentRowValue;
    const remaining = Math.max(0, amountNum - otherAllocations);
    const next = Math.min(remaining, lineOutstanding);
    handlePerRowAllocate(key, round2(next));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setValidationError('');
    setError('');

    if (!amountValid) {
      setValidationError('Amount must be greater than 0');
      return;
    }
    if (!counterpartyId) {
      setValidationError('Pick a counterparty');
      return;
    }
    if (!accountId) {
      setValidationError('Pick an account');
      return;
    }
    if (!allocationsMatch) {
      setValidationError('Allocations must sum to the amount');
      return;
    }

    const linesPayload = outstandingLines
      .map((line) => {
        const amt = Number(allocations[allocKey(line)]);
        if (!Number.isFinite(amt) || amt <= 0) return null;
        return {
          booking_id: line.booking_id,
          // Backend requires `operator_id` for direction='out' on per-row
          // lines so it can credit the right operator subledger.
          operator_id: counterpartyKind === 'operator' ? counterpartyId : undefined,
          order_line_key: line.order_line_key,
          gross_amount: amt,
          fee: 0,
          net_amount: amt,
          currency: line.currency || currency,
        };
      })
      .filter(Boolean);

    if (linesPayload.length === 0) {
      setValidationError('Enter at least one non-zero allocation.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        direction,
        account_id: accountId,
        gross_amount: amountNum,
        fee: 0,
        currency,
        occurred_at: occurredAtIso(occurredAt),
        counterparty: { kind: counterpartyKind, id: counterpartyId },
        lines: linesPayload,
        external_refs: bankReference ? { bank_reference: bankReference } : undefined,
        notes: notes || undefined,
      };
      const created = await PaymentsService.recordPayment(payload);
      if (onRecorded) onRecorded(created);
      if (onHide) onHide();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to record bulk payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    if (onHide) onHide();
  };

  // --- Render helpers -------------------------------------------------

  const renderCounterpartyPicker = () => {
    if (counterpartyLocked) {
      return (
        <div className="alert alert-info py-2 mb-0 small" role="status">
          <i className="fa fa-lock me-1" />
          <strong>{presetCounterparty.name || presetCounterparty.id}</strong>
          {' '}
          (
          {presetCounterparty.kind}
          )
        </div>
      );
    }
    if (counterpartyKind === 'partner') {
      return (
        <Form.Select
          value={counterpartyId}
          onChange={(e) => setCounterpartyId(e.target.value)}
          disabled={loadingPartners}
          aria-label="Partner"
        >
          <option value="">
            {loadingPartners ? 'Loading partners…' : 'Select partner…'}
          </option>
          {partners.map((p) => {
            const id = counterpartyIdOf(p);
            return (
              <option key={id} value={id}>
                {p.name || id}
              </option>
            );
          })}
        </Form.Select>
      );
    }
    return (
      <Form.Select
        value={counterpartyId}
        onChange={(e) => setCounterpartyId(e.target.value)}
        disabled={loadingOperators}
        aria-label="Operator"
      >
        <option value="">
          {loadingOperators ? 'Loading operators…' : 'Select operator…'}
        </option>
        {operators.map((o) => {
          const id = counterpartyIdOf(o);
          return (
            <option key={id} value={id}>
              {o.name || id}
            </option>
          );
        })}
      </Form.Select>
    );
  };

  const renderLinesTable = () => {
    if (!counterpartyId) {
      return (
        <div className="small text-muted">
          Pick a counterparty above to load outstanding lines.
        </div>
      );
    }
    if (linesLoading) {
      return (
        <div className="d-flex align-items-center small text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading outstanding lines…
        </div>
      );
    }
    if (linesError) {
      return <div className="alert alert-warning py-2 mb-0 small">{linesError}</div>;
    }
    if (outstandingLines.length === 0) {
      return (
        <div className="alert alert-secondary py-2 mb-0 small" role="status">
          No outstanding lines for this counterparty in this period.
        </div>
      );
    }
    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="small text-muted">
            {outstandingLines.length}
            {' '}
            outstanding line
            {outstandingLines.length === 1 ? '' : 's'}
            {' '}
            — sorted oldest first
          </div>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={autoAllocateTopDown}
            disabled={!amountValid}
          >
            <i className="fa fa-arrow-down me-1" />
            Auto-allocate top-down
          </Button>
        </div>
        {truncated && (
          <div className="alert alert-warning py-2 mb-2 small" role="alert">
            Showing first
            {' '}
            {outstandingLines.length}
            {' '}
            outstanding lines — narrow your period to see more.
          </div>
        )}
        <Table size="sm" responsive className="mb-2">
          <thead>
            <tr>
              <th>Booking ref</th>
              <th>Date</th>
              <th>Description</th>
              <th className="text-end">Owed</th>
              <th className="text-end">Paid so far</th>
              <th className="text-end">Outstanding</th>
              <th style={{ width: 180 }} className="text-end">Allocate now</th>
            </tr>
          </thead>
          <tbody>
            {outstandingLines.map((line, idx) => {
              const key = allocKey(line);
              const val = allocations[key];
              const valNum = Number(val);
              const lineOutstanding = Number(line.outstanding_amount) || 0;
              const overAllocated = Number.isFinite(valNum) && valNum > lineOutstanding + 0.005;
              // Currency mismatch: backend exempts cash_from_client
              // lines from the currency filter so they surface for
              // visibility even when the payment currency doesn't
              // match (e.g. EUR cash on a HUF-settling operator's
              // bookings). Disable allocation on these rows and
              // explain why; the auto-allocator also skips them.
              const currencyMismatch = !!line.currency && line.currency !== currency;
              const allocateControl = (
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  size="sm"
                  value={val ?? ''}
                  isInvalid={overAllocated}
                  disabled={currencyMismatch}
                  onChange={(ev) => handlePerRowAllocate(
                    key,
                    ev.target.value,
                  )}
                  aria-label={`Line ${idx + 1} allocation`}
                />
              );
              return (
                <tr key={key}>
                  <td>
                    <code className="small" title={line.booking_id}>
                      {line.booking_reference || line.booking_id}
                    </code>
                  </td>
                  <td className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                    {line.date ? formatDate(line.date) : '—'}
                  </td>
                  <td className="small">
                    {line.description || line.kind || '—'}
                    {line.full_name && (
                      <div className="text-muted" style={{ fontSize: '0.8em' }}>
                        {line.full_name}
                      </div>
                    )}
                  </td>
                  <td className="text-end small">
                    {formatMoney(line.owed_amount, line.currency || currency)}
                  </td>
                  <td className="text-end small text-muted">
                    {formatMoney(line.paid_amount, line.currency || currency)}
                  </td>
                  <td className="text-end small fw-bold">
                    {formatMoney(line.outstanding_amount, line.currency || currency)}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      {currencyMismatch ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={(
                            <Tooltip>
                              {`Change payment currency to ${line.currency} to allocate this line.`}
                            </Tooltip>
                          )}
                        >
                          {/* span wrapper needed for OverlayTrigger on a disabled control */}
                          <span className="d-inline-block">{allocateControl}</span>
                        </OverlayTrigger>
                      ) : allocateControl}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => handleRowMax(line)}
                        disabled={!amountValid || currencyMismatch}
                        aria-label={`Line ${idx + 1} max`}
                        title={currencyMismatch
                          ? `Change payment currency to ${line.currency} to allocate this line.`
                          : 'Fill with min(remaining unallocated, line outstanding)'}
                      >
                        max
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <div className="d-flex flex-wrap gap-3 justify-content-end small">
          <div className={allocationsMatch ? 'text-success' : 'text-danger'}>
            Allocated:
            {' '}
            <strong>{formatMoney(allocationsTotal, currency)}</strong>
            {' '}
            /
            {' '}
            {formatMoney(amountNum || 0, currency)}
          </div>
          <div className="text-muted">
            Still outstanding after this payment:
            {' '}
            <strong>{formatMoney(stillOutstandingAfter, currency)}</strong>
          </div>
        </div>
        {hasOverflow && (
          <div className="alert alert-warning py-2 mt-2 mb-0 small" role="alert">
            Amount exceeds total outstanding by
            {' '}
            <strong>{formatMoney(overflowAmount, currency)}</strong>
            {' '}
            — adjust the amount or it&apos;ll be unallocated overflow.
          </div>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={handleCancel} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-layer-group me-2" />
          Bulk Payment
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />
        {validationError && (
          <div className="alert alert-warning py-2 mb-3 small" role="alert">
            {validationError}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          {/* A) Counterparty + period + direction */}
          <Card className="mb-3">
            <Card.Body>
              <Card.Title as="h6" className="mb-3">Who & when</Card.Title>

              <Form.Group className="mb-3">
                <Form.Label className="d-block">Counterparty kind *</Form.Label>
                <Form.Check
                  inline
                  type="radio"
                  id="bpm_kind_operator"
                  name="bpm_kind"
                  label="Operator"
                  checked={counterpartyKind === 'operator'}
                  disabled={counterpartyLocked}
                  onChange={() => handleKindChange('operator')}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="bpm_kind_partner"
                  name="bpm_kind"
                  label="Partner"
                  checked={counterpartyKind === 'partner'}
                  disabled={counterpartyLocked}
                  onChange={() => handleKindChange('partner')}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  {counterpartyKind === 'operator' ? 'Operator *' : 'Partner *'}
                </Form.Label>
                {renderCounterpartyPicker()}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="d-block">Direction *</Form.Label>
                <Form.Check
                  inline
                  type="radio"
                  id="bpm_dir_in"
                  name="bpm_direction"
                  label="In (we receive)"
                  checked={direction === 'in'}
                  onChange={() => setDirection('in')}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="bpm_dir_out"
                  name="bpm_direction"
                  label="Out (we pay)"
                  checked={direction === 'out'}
                  onChange={() => setDirection('out')}
                />
              </Form.Group>

              <div className="row g-2">
                <Form.Group className="col-md-4" controlId="bpm_period_from">
                  <Form.Label>From *</Form.Label>
                  <Form.Control
                    type="date"
                    value={periodFrom}
                    onChange={(e) => setPeriodFrom(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="col-md-4" controlId="bpm_period_to">
                  <Form.Label>To *</Form.Label>
                  <Form.Control
                    type="date"
                    value={periodTo}
                    onChange={(e) => setPeriodTo(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="col-md-4" controlId="bpm_currency">
                  <Form.Label>Currency *</Form.Label>
                  <Form.Select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </Card.Body>
          </Card>

          {/* B) Money */}
          <Card className="mb-3">
            <Card.Body>
              <Card.Title as="h6" className="mb-3">Money</Card.Title>
              <div className="row g-2">
                <Form.Group className="col-md-6" controlId="bpm_account">
                  <Form.Label>Account *</Form.Label>
                  <Form.Select
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                  >
                    <option value="">Select account…</option>
                    {activeAccounts.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Where the money moved through (bank, cash, …).
                  </Form.Text>
                </Form.Group>
                <Form.Group className="col-md-3" controlId="bpm_amount">
                  <Form.Label>Amount *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="col-md-3" controlId="bpm_occurred_at">
                  <Form.Label>Occurred on *</Form.Label>
                  <Form.Control
                    type="date"
                    value={occurredAt}
                    onChange={(e) => setOccurredAt(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className="row g-2 mt-2">
                <Form.Group className="col-md-6" controlId="bpm_bank_reference">
                  <Form.Label>Bank reference</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. statement line ID"
                    value={bankReference}
                    onChange={(e) => setBankReference(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="col-md-6" controlId="bpm_notes">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Form.Group>
              </div>
            </Card.Body>
          </Card>

          {/* C) Outstanding lines */}
          <Card className="mb-3">
            <Card.Body>
              <Card.Title as="h6" className="mb-3">Outstanding lines</Card.Title>
              {renderLinesTable()}
            </Card.Body>
          </Card>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? 'Recording…' : 'Record Bulk Payment'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default BulkPaymentModal;
